import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useSearchProvidersQuery } from '../store/hooks';
import { useGetV2AdminOrganizationsQuery } from '../store/hooks';
import {
  useCopyAdminNoteTemplateMutation,
  type EffectiveNoteTemplateSummary,
} from '../store/hooks';

interface CopyTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  template: EffectiveNoteTemplateSummary | null;
}

export function CopyTemplateDialog({ open, onClose, template }: CopyTemplateDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [targetUserId, setTargetUserId] = useState<string | null>(null);
  const [targetOrgId, setTargetOrgId] = useState<string | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [orgSearchQuery, setOrgSearchQuery] = useState('');

  const [copyTemplate, { isLoading: isCopying }] = useCopyAdminNoteTemplateMutation();

  // Search providers/users
  const { data: providersData, isLoading: isSearchingProviders } = useSearchProvidersQuery(
    userSearchQuery || ' ',
    { skip: !userSearchQuery || userSearchQuery.length < 2 },
  );

  // Search organizations
  const { data: orgsData, isLoading: isSearchingOrgs } = useGetV2AdminOrganizationsQuery(
    {},
    { skip: !orgSearchQuery || orgSearchQuery.length < 2 },
  );

  const handleCopy = useCallback(async () => {
    if (!template || !targetUserId) {
      enqueueSnackbar('Please select a target user', { variant: 'error' });
      return;
    }

    try {
      await copyTemplate({
        adminCopyNoteTemplateRequest: {
          template_id: template.template?.id || '',
          target_user_id: targetUserId,
          target_organization_id: targetOrgId || null,
        },
      }).unwrap();

      enqueueSnackbar('Template copied successfully', { variant: 'success' });
      handleClose();
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.message || 'Failed to copy template',
        { variant: 'error' },
      );
    }
  }, [template, targetUserId, targetOrgId, copyTemplate, enqueueSnackbar]);

  const handleClose = useCallback(() => {
    setTargetUserId(null);
    setTargetOrgId(null);
    setUserSearchQuery('');
    setOrgSearchQuery('');
    onClose();
  }, [onClose]);

  const providers = providersData || [];
  const organizations = orgsData?.items || [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy Template to User</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {template && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Template
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {template.revision?.title || 'Unnamed Template'}
              </Typography>
              {template.revision?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {template.revision.description}
                </Typography>
              )}
            </Box>
          )}

          <Autocomplete
            options={providers}
            getOptionLabel={(option: any) => option.name || option.provider_id}
            loading={isSearchingProviders}
            onInputChange={(_, value) => setUserSearchQuery(value)}
            onChange={(_, value: any) => setTargetUserId(value?.provider_id || null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target User *"
                placeholder="Search for a user..."
                helperText="Select the user to copy this template to"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearchingProviders ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Autocomplete
            options={organizations}
            getOptionLabel={(option: any) => option.name || option.organization_id}
            loading={isSearchingOrgs}
            onInputChange={(_, value) => setOrgSearchQuery(value)}
            onChange={(_, value: any) => setTargetOrgId(value?.organization_id || null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target Organization (Optional)"
                placeholder="Search for an organization..."
                helperText="Optionally assign the copied template to an organization"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearchingOrgs ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Alert severity="info">
            This will create a copy of the template for the selected user. The original template
            will remain unchanged.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCopying}>
          Cancel
        </Button>
        <Button
          onClick={handleCopy}
          variant="contained"
          disabled={!targetUserId || isCopying}
        >
          {isCopying ? <CircularProgress size={20} /> : 'Copy Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

